import { html } from "htm/preact";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import { TERMS } from "../copy.js";
import { currentPath, navigate } from "../router.js";
import { getClient } from "../client.js";
import { getLastRound, type AppMode } from "../storage.js";
import { getIdentity, setAppMode, type AppIdentity } from "../identity.js";
import { Wordmark } from "./Brand.js";
import { Icon, type IconName } from "./Icon.js";
import { resolvedTheme, themePref, toggleTheme } from "../theme.js";

export type Tab = "scorecard" | "leaderboard" | "setup" | "clubhouse" | null;

/**
 * Wraps every screen so there is always a way out: a home button in the top right, and
 * — once you're inside a game — a bottom bar for the three screens you actually move
 * between while playing. The bar is at the bottom deliberately: this gets used
 * one-handed on a course, so those targets belong under your thumb. Home isn't one of
 * them; it's a way out, not somewhere you go mid-round.
 *
 * The home button is on every screen including home itself. Hiding it there saves
 * nothing and costs the one thing a persistent control is for — being in the same
 * place every time you reach for it, so you never have to look first.
 */
export function AppShell({
  code,
  activeTab,
  bare = false,
  children,
}: {
  code: string | null;
  activeTab: Tab;
  /** Drops the app bar and the tab bar entirely. The clubhouse screen asks for this:
   * it is meant to be looked at from across a room, and chrome sized for a thumb is
   * just clutter on a screen nobody is going to touch. It carries its own controls. */
  bare?: boolean;
  children: unknown;
}) {
  const atHome = code === null && (currentPath.value === "/" || currentPath.value === "");
  const main = useRef<HTMLElement | null>(null);

  // Replays the entrance animation on every route change. <main> isn't keyed on the
  // path deliberately: keying would remount the screen and throw away local state —
  // the hole you were on in the scorecard, most obviously — just to get a fade.
  useLayoutEffect(() => {
    const el = main.current;
    if (!el) return;
    el.classList.remove("screen-enter");
    void el.offsetWidth; // forces a reflow, which is what lets the animation restart
    el.classList.add("screen-enter");
  }, [currentPath.value]);

  if (bare) return html`<main ref=${main} class="bare">${children}</main>`;

  return html`
    <header class="app-bar">
      <${Wordmark} onClick=${() => navigate("/")} />
      <span class="app-bar-right">
        ${code && html`<${GameBadge} code=${code} />`}
        <${ThemeToggle} />
        <button
          class=${`icon-btn ${atHome ? "is-current" : ""}`}
          onClick=${() => navigate("/")}
          aria-label="Home"
          aria-current=${atHome ? "page" : undefined}
          title="Home"
        >
          <${Icon} name="home" />
        </button>
      </span>
    </header>

    <main ref=${main}>${children}</main>

    ${code && html`<${GameTabs} code=${code} activeTab=${activeTab} />`}
  `;
}

/** Reading `themePref.value` here is what re-renders the button when the theme flips. */
function ThemeToggle() {
  themePref.value;
  const goingTo = resolvedTheme() === "dark" ? "light" : "dark";

  return html`
    <button
      class="icon-btn"
      onClick=${toggleTheme}
      aria-label=${`Switch to ${goingTo} theme`}
      title=${`Switch to ${goingTo} theme`}
    >
      <${Icon} name=${goingTo === "dark" ? "moon" : "sun"} />
    </button>
  `;
}

/** Says which game you're in and who the app thinks you are — so it's obvious you're
 * inside a game rather than browsing, and obvious which player your scores land on.
 *
 * For an organiser playing in their own game it is also the way between the two, which
 * is why it's here rather than on a screen: the switch has to be within reach halfway
 * down a round, and going somewhere to find it would throw away the hole the scorecard
 * was sitting on. */
function GameBadge({ code }: { code: string }) {
  const identity = getIdentity(code);
  const snapshot = getClient(code)?.snapshot.value ?? null;
  const [open, setOpen] = useState(false);
  const seatName =
    snapshot?.tournament.players.find((p) => p.id === identity?.seatId)?.name ?? null;

  const me =
    identity?.role === "spectator"
      ? "Watching"
      : identity?.canOrganize
        ? TERMS.Organizer
        : (seatName ?? (identity?.isOrganizer ? TERMS.Organizer : null));

  const label = html`
    ${me && html`<span class="app-me">${me}</span>`}
    <span class="app-code">${code}</span>
  `;

  if (!identity?.canSwitch) return html`<span class="app-identity">${label}</span>`;

  return html`
    <span class="app-identity-wrap">
      <button
        class="app-identity app-identity-btn"
        onClick=${() => setOpen((v) => !v)}
        aria-expanded=${open}
        aria-haspopup="true"
        title="Switch between organizing and playing"
      >
        ${label}
        <!-- Stays lit in player mode too. This is the only thing on screen still saying
             the device holds the organiser's token, and an organiser who can't see that
             has no reason to think there's a way back. -->
        <span class="app-organizer-dot" aria-hidden="true"></span>
      </button>
      ${open && html`<${IdentitySwitcher} code=${code} seatName=${seatName} identity=${identity} onClose=${() => setOpen(false)} />`}
    </span>
  `;
}

/** The two hats, listed. App-bar chrome rather than a screen of its own — see the note
 * on `GameBadge` about why this can't be a route. */
function IdentitySwitcher({
  code,
  seatName,
  identity,
  onClose,
}: {
  code: string;
  seatName: string | null;
  identity: AppIdentity;
  onClose: () => void;
}) {
  function pick(mode: AppMode) {
    setAppMode(code, mode);
    onClose();
  }

  const option = (mode: AppMode, title: string, help: string) => html`
    <button
      class=${`identity-option ${identity.mode === mode ? "is-current" : ""}`}
      aria-current=${identity.mode === mode ? "true" : undefined}
      onClick=${() => pick(mode)}
    >
      <${Icon} name=${mode === "organizer" ? "sliders" : "flag"} size=${18} />
      <span class="list-main">
        <span>${title}</span>
        <span class="list-sub">${help}</span>
      </span>
      ${identity.mode === mode && html`<${Icon} name="check" size=${16} />`}
    </button>
  `;

  return html`
    <!-- Closes on any click that lands outside, including one meant for something else.
         Losing a tap is a smaller cost than a menu that follows you around the app. -->
    <div class="identity-backdrop" onClick=${onClose}></div>
    <div class="identity-menu" role="menu">
      ${option("organizer", TERMS.Organizer, "Setup, and every score on the card")}
      ${option("player", `Playing as ${seatName ?? "you"}`, "Only your own scores")}
    </div>
  `;
}

function GameTabs({ code, activeTab }: { code: string; activeTab: Tab }) {
  const identity = getIdentity(code);
  // `canOrganize`, not `isOrganizer`: an organiser who has switched to playing wants
  // the app a player gets, and setup isn't part of it until they switch back.
  const isAdmin = identity?.canOrganize === true;
  const isSpectator = identity?.role === "spectator";
  const snapshot = getClient(code)?.snapshot.value ?? null;
  const rounds = snapshot?.tournament.rounds ?? [];

  // Prefer the round you were last scoring; otherwise the first one. With no rounds
  // yet there's nothing to score, so that tab points at setup instead.
  const lastRound = getLastRound(code);
  const targetRound = rounds.find((r) => r.id === lastRound) ?? rounds[0] ?? null;

  const tabs: { id: Tab; label: string; icon: IconName; to: string; show: boolean }[] = [
    // Spectators lead with the clubhouse screen: it is the reason to be here, and it
    // is the only tab of theirs that isn't a page built for someone who is playing.
    {
      id: "clubhouse",
      label: "Clubhouse",
      icon: "eye",
      to: `/t/${code}/clubhouse`,
      show: isSpectator,
    },
    {
      id: "scorecard",
      // A spectator can't score on it, so calling it the scorecard promises an
      // action they don't have. It's the cards, to look at.
      label: isSpectator ? "Cards" : "Scorecard",
      icon: "flag",
      to: targetRound ? `/t/${code}/round/${targetRound.id}` : `/t/${code}/setup`,
      // With no rounds yet this tab falls back to setup, which a spectator can't
      // open — so there is nothing behind it for them until a round exists.
      show: !isSpectator || targetRound !== null,
    },
    {
      id: "leaderboard",
      label: "Standings",
      icon: "trophy",
      to: `/t/${code}/leaderboard`,
      show: true,
    },
    { id: "setup", label: "Setup", icon: "sliders", to: `/t/${code}/setup`, show: isAdmin },
  ];

  return html`
    <nav class="tab-bar" aria-label=${`${TERMS.Event} navigation`}>
      ${tabs
        .filter((t) => t.show)
        .map(
          (t) => html`
            <button
              key=${t.id}
              class=${`tab ${activeTab === t.id ? "active" : ""}`}
              aria-current=${activeTab === t.id ? "page" : undefined}
              onClick=${() => navigate(t.to)}
            >
              <${Icon} name=${t.icon} size=${22} />
              <span class="tab-label">${t.label}</span>
            </button>
          `,
        )}
    </nav>
  `;
}
